/**
 * Professional Flashcard Component
 * - Multiple design templates (3 styles)
 * - 3D animations and transitions
 * - Sound effects
 * - Professional styling
 */

import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Volume2, VolumeX, RotateCw, Sparkles, Zap, Star } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import type { Flashcard } from '../../types/flashcard'

interface ProfessionalFlashcardProps {
  flashcard: Flashcard
  isFlipped: boolean
  onFlip: () => void
  designVariant?: 1 | 2 | 3
  enableSound?: boolean
  onSoundToggle?: (enabled: boolean) => void
}

// Sound effects
const playSound = (type: 'flip' | 'success' | 'error') => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    
    const createTone = (frequency: number, duration: number, type: OscillatorType = 'sine') => {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
      oscillator.type = type
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + duration)
    }
    
    switch (type) {
      case 'flip':
        createTone(440, 0.1) // A4 note
        setTimeout(() => createTone(554, 0.1), 50) // C#5 note
        break
      case 'success':
        createTone(523, 0.2) // C5 note
        setTimeout(() => createTone(659, 0.2), 100) // E5 note
        setTimeout(() => createTone(784, 0.3), 200) // G5 note
        break
      case 'error':
        createTone(200, 0.3, 'sawtooth') // Low frequency
        break
    }
  } catch (error) {
    console.log('Audio not supported')
  }
}

const ProfessionalFlashcard: React.FC<ProfessionalFlashcardProps> = ({
  flashcard,
  isFlipped,
  onFlip,
  designVariant = 1,
  enableSound = true,
  onSoundToggle
}) => {
  const [soundEnabled, setSoundEnabled] = useState(enableSound)
  const [isHovered, setIsHovered] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Handle sound toggle
  const toggleSound = () => {
    const newSoundState = !soundEnabled
    setSoundEnabled(newSoundState)
    onSoundToggle?.(newSoundState)
  }

  // Handle flip with sound
  const handleFlip = () => {
    if (soundEnabled) {
      playSound('flip')
    }
    onFlip()
  }

  // Get design variant styles
  const getDesignStyles = (variant: 1 | 2 | 3, isFront: boolean) => {
    const baseStyles = "absolute inset-0 rounded-3xl shadow-2xl p-8 flex flex-col justify-center items-center text-center backface-hidden border-4"
    
    switch (variant) {
      case 1: // Classic Professional
        return {
          className: `${baseStyles} ${isFront ? 'border-amber-400' : 'border-blue-400'}`,
          style: {
            background: isFront 
              ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #f59e0b 100%)'
              : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 50%, #3b82f6 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
          }
        }
      
      case 2: // Modern Gradient
        return {
          className: `${baseStyles} ${isFront ? 'border-purple-400' : 'border-emerald-400'}`,
          style: {
            background: isFront
              ? 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 50%, #a855f7 100%)'
              : 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #10b981 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
          }
        }
      
      case 3: // Elegant Glass
        return {
          className: `${baseStyles} border-white/30`,
          style: {
            background: isFront
              ? 'linear-gradient(135deg, rgba(255, 243, 199, 0.8) 0%, rgba(253, 230, 138, 0.8) 100%)'
              : 'linear-gradient(135deg, rgba(219, 234, 254, 0.8) 0%, rgba(191, 219, 254, 0.8) 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }
      
      default:
        return getDesignStyles(1, isFront)
    }
  }

  // Get text color based on design
  const getTextColor = (variant: 1 | 2 | 3, isFront: boolean) => {
    switch (variant) {
      case 1: return 'text-blue-900'
      case 2: return isFront ? 'text-purple-900' : 'text-emerald-900'
      case 3: return 'text-gray-900'
      default: return 'text-blue-900'
    }
  }

  // Get difficulty indicator
  const getDifficultyIndicator = (difficulty: 'easy' | 'medium' | 'hard') => {
    const indicators = {
      'easy': { icon: Star, color: 'text-green-500', bg: 'bg-green-100' },
      'medium': { icon: Zap, color: 'text-yellow-500', bg: 'bg-yellow-100' },
      'hard': { icon: Sparkles, color: 'text-red-500', bg: 'bg-red-100' }
    }
    
    // Fallback to 'medium' if difficulty is invalid
    const indicator = indicators[difficulty] || indicators['medium']
    const { icon: Icon, color, bg } = indicator
    
    return (
      <div className={`absolute top-4 right-4 ${bg} rounded-full p-2`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
    )
  }

  const frontStyles = getDesignStyles(designVariant, true)
  const backStyles = getDesignStyles(designVariant, false)
  const textColor = getTextColor(designVariant, isFlipped)

  return (
    <div className="relative w-full h-full perspective-1000">
      {/* Sound Toggle */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={toggleSound}
          className={`p-2 rounded-full transition-all ${
            soundEnabled 
              ? 'bg-blue-500 text-white shadow-lg' 
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title={soundEnabled ? 'Tắt âm thanh' : 'Bật âm thanh'}
        >
          {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>

      {/* Difficulty Indicator */}
      {getDifficultyIndicator(flashcard.difficulty)}

      {/* Main Card */}
      <motion.div
        ref={cardRef}
        className="relative w-[700px] h-[500px] cursor-pointer"
        onClick={handleFlip}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        animate={{ 
          rotateY: isFlipped ? 180 : 0,
          scale: isHovered ? 1.02 : 1,
          rotateX: isHovered ? 5 : 0
        }}
        transition={{ 
          duration: 0.8,
          type: "spring",
          stiffness: 100,
          damping: 15
        }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front Side */}
        <motion.div
          className={`${frontStyles.className} ${isFlipped ? 'invisible' : 'visible'}`}
          style={{
            ...frontStyles.style,
            backfaceVisibility: 'hidden',
            transform: 'rotateY(0deg)',
            padding: '3rem 2rem'
          }}
          initial={{ opacity: 0, rotateY: -90 }}
          animate={{ opacity: 1, rotateY: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="w-full h-full flex flex-col justify-center items-center relative">
            {/* Decorative Elements */}
            <div className="absolute top-6 left-6 w-8 h-8 bg-white/20 rounded-full"></div>
            <div className="absolute bottom-6 right-6 w-6 h-6 bg-white/20 rounded-full"></div>
            
            <div className={`${textColor} px-8 text-center relative z-10`}>
              <KaTeXRenderer 
                content={flashcard.front} 
                className="text-4xl md:text-5xl font-bold leading-relaxed" 
              />
            </div>
            
            <motion.p 
              className="text-base mt-8 text-blue-600 font-medium"
              animate={{ opacity: isHovered ? 1 : 0.7 }}
              transition={{ duration: 0.3 }}
            >
              ↻ Click để xem câu trả lời
            </motion.p>
          </div>
        </motion.div>

        {/* Back Side */}
        <motion.div
          className={`${backStyles.className} ${isFlipped ? 'visible' : 'invisible'}`}
          style={{
            ...backStyles.style,
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            padding: '3rem 2rem'
          }}
          initial={{ opacity: 0, rotateY: 90 }}
          animate={{ opacity: 1, rotateY: 180 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="w-full h-full flex flex-col justify-center items-center relative">
            {/* Decorative Elements */}
            <div className="absolute top-6 left-6 w-8 h-8 bg-white/20 rounded-full"></div>
            <div className="absolute bottom-6 right-6 w-6 h-6 bg-white/20 rounded-full"></div>
            
            <div className={`${textColor} px-8 text-center relative z-10`}>
              <KaTeXRenderer 
                content={flashcard.back} 
                className="text-3xl md:text-4xl font-medium leading-relaxed whitespace-pre-wrap" 
              />
            </div>
            
            {/* Tags - Hidden as requested */}
            {false && flashcard.tags && flashcard.tags.length > 0 && (
              <motion.div 
                className="mt-4 flex flex-wrap justify-center gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {flashcard.tags.slice(0, 3).map((tag, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 bg-white/30 border border-white/50 rounded-full text-sm font-semibold backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>

    </div>
  )
}

export default ProfessionalFlashcard
